import React, { useContext, useEffect, useRef } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Typography
} from '@one-thd/sui-atomic-components';
import { StoreFinderRedirector } from '@thd-nucleus/app-render';
import { QueryContext, extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { EmtCarousel } from '@thd-olt-component-react/card-carousel';
import {
  GrandOpeningContainer,
  StoreDetailsContent,
  StoreDetailsFAQ,
  StoreDetailsRecycleProgram,
  PromoPod
} from '@thd-olt-component-react/emt-generated-content';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { StoreDetailMetadata } from '@thd-olt-component-react/metadata';
/*
  These imports might change once all the experience are migrated to vite.
  Currently they have webpack and vite components co-existing.
*/
import {
  StoreRatingsAndReviewsDesktopImporterVite as StoreRatingsAndReviewsDesktopImporter,
  StoreRatingsAndReviewsMobileImporterVite as StoreRatingsAndReviewsMobileImporter
} from '@thd-olt-component-react/ratings-and-reviews';
import { PopularCategories } from '@thd-olt-component-react/related-search';
import { useLocation, useParams } from '@thd-olt-component-react/router';
import { SpecialBuyBanner } from '@thd-olt-component-react/special-buy-banner';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import {
  StoreHero,
  StoreHeroCurbside,
  StoreHeroDetails,
  StoreHeroFeatures,
  StoreHeroHeader,
  StoreHeroWorkshop
} from '@thd-olt-component-react/store-hero';
import { StorePod, StorePodServices } from '@thd-olt-component-react/store-pod';
import { CustomerContext } from '@thd-olt-functional/customer-information';

import { NearbyStores } from '../../shared-components/NearbyStores';
import { StoreFinderBreadcrumbs } from '../../shared-components/StoreFinderBreadcrumbs';
import { useMobileBreakpoint } from '../../utils/stylesUtils';
import { PPSMainRecycling } from '../../shared-components/PPSMainRecycling/PPSMainRecycling';

export const Details = () => {
  declareContexts([QueryContext, ExperienceContext, CustomerContext], { freeze: true });

  const { channel } = useContext(ExperienceContext);
  const path = useLocation();
  const ratingsReviewsRef = useRef();
  const {
    storeNumber, storeZip, name, state, city
  } = useParams();
  const isMobileBreakpoint = useMobileBreakpoint();

  const isMobileChannel = channel === 'mobile';

  const onRatingClick = () => {
    ratingsReviewsRef.current.scrollIntoView();
  };

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('store-finder.details-page-load', {
      storeNumber,
      storeZip,
      state,
      name,
      city
    });
  }, [storeNumber]);

  const headerFont = {
    size: isMobileChannel ? 'medium' : 'xlarge'
  };

  return (
    <>
      <ErrorBoundary name="sf-details-redirector">
        <StoreFinderRedirector />
      </ErrorBoundary>

      <ErrorBoundary name="sf-details-metadata">
        <StoreDetailMetadata storeId={storeNumber} />
      </ErrorBoundary>

      <div className="sui-max-w-screen-2xl sui-m-auto">
        <div className="sui-flex sui-flex-col sui-px-2 2xl:sui-px-0">
          <ErrorBoundary name="store-detail-breadcrumbs">
            <StoreFinderBreadcrumbs includeDirectory />
          </ErrorBoundary>
          <ErrorBoundary name="store-details-grand-opening-pod">
            <GrandOpeningContainer
              contentId={storeNumber}
              pageName="storeDetails"
            />
          </ErrorBoundary>
          <ErrorBoundary name="store-detail-header">
            <StoreHero storeId={storeNumber}>
              <StoreHeroHeader />
              <section className="sui-flex sui-flex-col lg:sui-grid lg:sui-grid-cols-3 sui-gap-3">
                <StoreHeroDetails contentId={storeNumber} pageName="storeDetails" />
                <StoreHeroCurbside />
                {isMobileChannel && <StoreHeroWorkshop />}
                <StoreHeroFeatures showServices={isMobileBreakpoint} />
              </section>
            </StoreHero>
          </ErrorBoundary>

          <section className="sui-mt-8 sui-flex sui-flex-col sui-gap-8">
            <ErrorBoundary name="crawlable-carousel">
              <EmtCarousel
                contentId={storeNumber}
                pageName="storeDetails"
                headerTitle="Popular in Your Store"
              />
            </ErrorBoundary>

            <ErrorBoundary name="store-details-promo-pod">
              <PromoPod contentId={storeNumber} pageName="storeDetails" />
            </ErrorBoundary>

            <section className="sui-grid sui-grid-cols-12 sui-gap-4">
              {!isMobileChannel && (
                <div className="sui-col-span-12 lg:sui-col-span-4">
                  <Card PaperProps={{ className: 'sui-size-full' }}>
                    <CardTitle
                      className="sui-px-2 sui-pt-4"
                      header={(<Typography variant="h2" weight="display">Store Features</Typography>)}
                    />
                    <CardBody disablePadding>
                      <StorePod storeId={storeNumber}>
                        <StorePodServices disablePlaceholder showIcon />
                      </StorePod>
                    </CardBody>
                  </Card>
                </div>
              )}

              <div className="sui-col-span-12 lg:sui-col-span-8">
                <ErrorBoundary name="store-details-content">
                  <Card PaperProps={{ className: 'sui-size-full' }}>
                    <CardBody disablePadding>
                      <StoreDetailsContent storeId={storeNumber} />
                    </CardBody>
                  </Card>
                </ErrorBoundary>
              </div>
            </section>

            <ErrorBoundary name="emt-details-faq">
              <StoreDetailsFAQ storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="store-details-recycle-program">
              <PPSMainRecycling storeId={storeNumber} />
            </ErrorBoundary>

            <ErrorBoundary name="store-detail-sponsored-carousel">
              <RetailMediaCarousel
                plaLocation="sponsoredCarouselStorePage"
                pageContext={{
                  schema: 'storepage_sponsored',
                  isStorePage: true
                }}
                mockEnabledFeatureSwitch
                dynamic={{
                  pageType: 'storeDetails'
                }}
                hydrator={{
                  className: 'grid',
                  delay: 1500,
                  scrollBuffer: 500
                }}
              />
            </ErrorBoundary>

            <ErrorBoundary name="store-detail-nearby-stores">
              {!isMobileChannel && (
                <Hydrator
                  id="store-details-nearby-stores-map"
                  scrollBuffer={500}
                >
                  <NearbyStores storeNumber={storeNumber} omitStoreNumber={storeNumber} />
                </Hydrator>
              )}
            </ErrorBoundary>

            <ErrorBoundary name="store-detail-ratings-reviews">
              <div ref={ratingsReviewsRef}>
                {!isMobileChannel && (
                  <StoreRatingsAndReviewsDesktopImporter
                    storeId={storeNumber}
                    category="store"
                    seoStoreLink={path.pathname}
                    dynamic={{
                      pageType: 'storeDetails'
                    }}
                    hydrator={{
                      className: 'sui-bg-primary',
                      scrollBuffer: 500
                    }}
                  />
                )}

                {isMobileChannel && (
                  <StoreRatingsAndReviewsMobileImporter
                    storeId={storeNumber}
                    category="store"
                    seoStoreLink={path.pathname}
                    dynamic={{
                      pageType: 'storeDetails'
                    }}
                    hydrator={{
                      className: 'sui-bg-primary',
                      scrollBuffer: 500
                    }}
                  />
                )}
              </div>
            </ErrorBoundary>

            <ErrorBoundary name="store-detail-nearby-stores">
              {isMobileChannel && (
                <Hydrator
                  id="store-details-nearby-stores-map"
                  scrollBuffer={500}
                >
                  <NearbyStores storeNumber={storeNumber} omitStoreNumber={storeNumber} />
                </Hydrator>
              )}
            </ErrorBoundary>

            <ErrorBoundary name="store-detail-special-buy">
              <Hydrator
                id="store-details-special-buy"
                scrollBuffer={800}
              >
                <SpecialBuyBanner specialBuyType="sbotd" />
              </Hydrator>
            </ErrorBoundary>

            <ErrorBoundary name="store-detail-popular-categories">
              <PopularCategories
                contentId={storeNumber}
                pageType="storeDetails"
              />
            </ErrorBoundary>

            <ErrorBoundary name="store-detail-explore-more">
              <EmtLinks path={path.pathname} />
            </ErrorBoundary>
          </section>
        </div>
      </div>
    </>
  );
};

Details.displayName = 'Details';
Details.propTypes = {};

Details.dataModel = extend(
  Breadcrumbs,
  EmtLinks,
  GrandOpeningContainer,
  NearbyStores,
  PopularCategories,
  PromoPod,
  SpecialBuyBanner,
  StoreDetailMetadata,
  StoreDetailsContent,
  StoreDetailsFAQ,
  StoreDetailsRecycleProgram,
  StoreFinderBreadcrumbs,
  StoreFinderRedirector,
  StoreHero,
  StorePod,
  StoreRatingsAndReviewsDesktopImporter,
  StoreRatingsAndReviewsMobileImporter
);
